import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, BarChart3, Users, Mail, FileText, Power, HelpCircle, Gamepad2, Terminal, Settings, Folder, Film, Monitor, Volume2, Sparkles, X, Wand2 } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

const menuItems = [
  { id: 'welcome', label: 'Velkommen', icon: Sparkles, section: 'Programmer', highlight: true },
  { id: 'appwizard', label: 'App-veiviser', icon: Wand2, section: 'Programmer', highlight: true },
  { id: 'services', label: 'Tjenester', icon: Cpu, section: 'Programmer' },
  { id: 'metrics', label: 'Statistikk', icon: BarChart3, section: 'Programmer' },
  { id: 'about', label: 'Om Oss', icon: Users, section: 'Programmer' },
  { id: 'contact', label: 'Kontakt', icon: Mail, section: 'Programmer' },
  { id: 'video', label: 'Demo Video', icon: Film, section: 'Programmer' },
];

const accessoriesItems = [
  { id: 'minesweeper', label: 'Minesveiper', icon: Gamepad2 },
  { id: 'cmd', label: 'Ledetekst', icon: Terminal },
];

// Settings Dialog Component
const SettingsDialog = ({ isOpen, onClose, crtEnabled, setCrtEnabled, volume, setVolume }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#c0c0c0',
          border: '3px solid',
          borderColor: '#fff #404040 #404040 #fff',
          width: '360px',
          boxShadow: '4px 4px 0 rgba(0,0,0,0.3)'
        }}
      >
        {/* Title bar */}
        <div style={{
          background: 'linear-gradient(90deg, #000080 0%, #1084d0 100%)',
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Settings size={14} color="white" />
            <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>
              Skjerminnstillinger
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '18px',
              height: '18px',
              background: '#c0c0c0',
              border: '2px solid',
              borderColor: '#fff #404040 #404040 #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <X size={10} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* CRT Effect Toggle */}
          <div style={{
            background: 'white',
            border: '2px solid',
            borderColor: '#808080 #fff #fff #808080',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Monitor size={20} color="#000080" />
              <span style={{ fontWeight: 'bold', fontSize: '13px' }}>CRT-skjermeffekt</span>
            </div>
            <p style={{ fontSize: '11px', color: '#555', margin: '0 0 12px 0' }}>
              Simulerer klassiske CRT-skannelinjer og subtil flimring for autentisk retro-følelse.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={crtEnabled}
                onChange={(e) => setCrtEnabled(e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '12px' }}>Aktiver CRT-effekt</span>
            </label>
          </div>

          {/* Volume */}
          <div style={{
            background: 'white',
            border: '2px solid',
            borderColor: '#808080 #fff #fff #808080',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Volume2 size={20} color="#000080" />
              <span style={{ fontWeight: 'bold', fontSize: '13px' }}>Lydvolum</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => setVolume(parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#666', marginTop: '4px' }}>
              <span>Av</span>
              <span>{volume}%</span>
              <span>Maks</span>
            </div>
          </div>

          {/* OK Button */}
          <button
            onClick={onClose}
            style={{
              marginTop: '16px',
              width: '100%',
              padding: '8px',
              background: '#c0c0c0',
              border: '2px solid',
              borderColor: '#fff #404040 #404040 #fff',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '12px'
            }}
          >
            OK
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function StartMenu({ 
  onClose, 
  onOpenWindow, 
  onShutdown, 
  onShowHelp,
  crtEnabled,
  setCrtEnabled,
  volume,
  setVolume
}) {
  const [showSettings, setShowSettings] = useState(false);

  const handleItemClick = (id) => {
    playSound(SOUNDS.click);
    onOpenWindow(id);
    onClose();
  };

  const handleShutdown = () => {
    playSound(SOUNDS.windowClose);
    onClose();
    setTimeout(() => onShutdown?.(), 300);
  };

  const handleSettings = () => {
    playSound(SOUNDS.click);
    setShowSettings(true);
  };

  const handleHelp = () => {
    playSound(SOUNDS.click);
    onClose();
    onShowHelp?.();
  };

  return (
    <>
      {/* Backdrop to close menu */}
      <div className="start-menu-backdrop" onClick={onClose} />
      
      <motion.div
        className="start-menu"
        initial={{ opacity: 0, y: 20, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ 
          duration: 0.2, 
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
      >
        {/* Blue sidebar */}
        <div className="start-menu-sidebar">
          <span className="sidebar-text">Autonomous<span>95</span></span>
        </div>

        {/* Menu content */}
        <div className="start-menu-content">
          <div className="start-menu-section">
            <div className="section-label">Programmer</div>
            {menuItems.map(item => (
              <button
                key={item.id}
                className={`start-menu-item ${item.highlight ? 'highlight' : ''}`}
                onClick={() => handleItemClick(item.id)}
              >
                <item.icon size={16} color={item.highlight ? '#ffd700' : undefined} />
                <span style={item.highlight ? { color: '#000080', fontWeight: 'bold' } : {}}>
                  {item.label}
                </span>
                {item.highlight && <span style={{ marginLeft: 'auto', fontSize: '10px' }}>⭐</span>}
              </button>
            ))}
          </div>

          <div className="start-menu-divider" />

          <div className="start-menu-section">
            <div className="section-label">Tilbehør</div>
            {accessoriesItems.map(item => (
              <button
                key={item.id}
                className="start-menu-item"
                onClick={() => handleItemClick(item.id)}
              >
                <item.icon size={16} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="start-menu-divider" />

          <button className="start-menu-item" onClick={handleSettings}>
            <Settings size={16} />
            <span>Innstillinger</span>
          </button>

          <button className="start-menu-item" onClick={handleHelp}>
            <HelpCircle size={16} />
            <span>Hjelp</span>
            <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#888' }}>?</span>
          </button>

          <div className="start-menu-divider" />

          <button className="start-menu-item shutdown" onClick={handleShutdown}>
            <Power size={16} />
            <span>Avslutt...</span>
          </button>
        </div>
      </motion.div>

      {/* Settings Dialog */}
      <AnimatePresence>
        {showSettings && (
          <SettingsDialog
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            crtEnabled={crtEnabled}
            setCrtEnabled={setCrtEnabled}
            volume={volume}
            setVolume={setVolume}
          />
        )}
      </AnimatePresence>
    </>
  );
}
